/**
 * ChatTransferVerificationCode.ts
 *
 * Derives the user-visible 6-digit verification code from the `transferKey`.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatTransferPairing.swift` →
 * `ChatTransferKDF.deriveVerificationNumber`. Same HKDF info string
 * `kaether:chat-transfer:verification:v1`, 4-byte output, big-endian
 * `uint32 % 1_000_000`, zero-padded to 6 digits.
 *
 * The web `info` string differs from the iOS file's `kaether:transfer:vfn:v1`
 * because the web spec in `Signal_standard_web.md` (K3) prescribes
 * `kaether:chat-transfer:verification:v1`. Both endpoints on the web must
 * therefore use the new info; the iOS side will be reconciled in a follow-up
 * (cross-platform transfer between iOS and web is not on the roadmap yet).
 *
 * Why an independent HKDF derivation rather than e.g. SHA256(transferKey)?
 *
 *   - The HKDF info string is **distinct** from the AEAD-key info. Showing
 *     the verification code on screen does not weaken the AEAD key because
 *     they are independent values derived from the same root.
 *   - Future protocol bumps can swap one info string without invalidating
 *     archived material encoded under the other.
 *
 * Constant-time-ish equality:
 *
 *   `verificationCodeEquals(a, b)` compares the two 6-character strings in
 *   constant time (no early return on first mismatch). For 6 ASCII digits
 *   the perf delta is negligible, but the security cosmetic matches iOS and
 *   keeps the user-facing surface aligned with the safety-number contract.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** HKDF info bound to v1 of the verification-code derivation. */
export const HKDF_INFO_VERIFICATION = new TextEncoder().encode(
  "kaether:chat-transfer:verification:v1",
);

const HKDF_SALT_ZERO_32 = new Uint8Array(32);
const CODE_BYTES = 4;
const CODE_MODULUS = 1_000_000;
export const VERIFICATION_CODE_LENGTH = 6;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Derive the 6-digit verification code from a 32-byte transfer key.
 *
 * Deterministic — repeated calls with the same key return the same string.
 * Both endpoints independently compute the same code from the symmetric
 * transferKey. A mismatch on the two screens is a MITM signal.
 */
export async function deriveVerificationCode(
  transferKey: Uint8Array,
): Promise<string> {
  const ikmKey = await crypto.subtle.importKey(
    "raw",
    transferKey,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: HKDF_SALT_ZERO_32,
      info: HKDF_INFO_VERIFICATION,
    },
    ikmKey,
    CODE_BYTES * 8,
  );
  const bytes = new Uint8Array(bits);
  // Big-endian so the byte order is canonical across architectures and
  // matches the iOS derivation. Cast to >>> 0 keeps the result in the
  // unsigned 32-bit range despite JS bitwise operations being signed.
  const value =
    (bytes[0] * 0x100_0000 +
      (bytes[1] << 16) +
      (bytes[2] << 8) +
      bytes[3]) >>>
    0;
  const mod = value % CODE_MODULUS;
  return mod.toString(10).padStart(VERIFICATION_CODE_LENGTH, "0");
}

/**
 * Constant-time-ish 6-digit equality compare. No early return on the first
 * mismatching byte.
 *
 * If either side is the wrong length the function returns false without
 * scanning (length is a non-secret because the wire format pins it).
 */
export function verificationCodeEquals(a: string, b: string): boolean {
  if (a.length !== VERIFICATION_CODE_LENGTH || b.length !== VERIFICATION_CODE_LENGTH) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < VERIFICATION_CODE_LENGTH; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}
